import { useState, useMemo, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { registerIdentity, type RegisterResult } from '../api/register';
import { login } from '../api/login';
import {
  validateRegisterForm,
  type RegisterFieldErrors,
} from '../utils/validation';
import {
  postRegisterDestination,
  resolveReturnUrl,
  isAbsoluteReturnUrl,
  loginDefaultReturnUrl,
} from '../utils/returnUrl';

// NOTE: copy here is functional/placeholder — voice + UX polish is uid-001-11
// (Menley), explicitly OUT of this story. Per conventions/frontend-patterns.md,
// navigation uses <Link> (not <button onClick={navigate}>) and every
// interactive element carries a data-testid.

// 'autologin-fallback' is the bl-792 graceful-degradation state: the account WAS
// created (201) but the immediate auto-login failed, so we surface a friendly
// "account created — please sign in" banner and hand the user off to /login
// rather than leaving them stranded on /register.
type SubmitStatus =
  | 'idle'
  | 'submitting'
  | 'autologin-fallback'
  | RegisterResult['kind'];

export default function RegisterPage() {
  const [searchParams] = useSearchParams();

  // The raw, untrusted return target carried from the calling suite. Accepts the
  // RegisterPage param names (`returnUrl` / `redirect_uri`). Never trusted
  // verbatim — every consumer below runs it through the open-redirect gate.
  const rawReturn = useMemo(
    () => searchParams.get('returnUrl') ?? searchParams.get('redirect_uri'),
    [searchParams],
  );

  // bl-792 — INTO-the-site auto-login target.
  //
  // On a successful register we auto-login (register mints no session) and carry
  // the brand-new user INTO the site via the SAME cross-domain `?code=` handoff
  // `/login` uses — NOT a bounce to `/login`. For a brand-new, unverified user
  // that lands on the bl-795 `/onboarding/verify-email` step behind the portal.
  //
  // `returnTarget` mirrors LoginPage exactly: an allowlisted cross-origin caller
  // is honored; an ABSENT param defaults to the DAO portal (the member's home)
  // so the auto-login still produces a cross-domain handoff INTO the site rather
  // than dead-ending centrally; an evil origin collapses to that safe default
  // via the shared open-redirect gate (`resolveReturnUrl`) — never reaching the
  // wire. `returnOrigin` is the scheme+host the server allowlists (origin-exact),
  // or undefined for a same-app relative target.
  const returnTarget = useMemo(() => {
    if (rawReturn == null) {
      return loginDefaultReturnUrl();
    }
    return resolveReturnUrl(rawReturn);
  }, [rawReturn]);

  const returnOrigin = useMemo(() => {
    if (!isAbsoluteReturnUrl(returnTarget)) {
      return undefined;
    }
    try {
      return new URL(returnTarget).origin;
    } catch {
      return undefined;
    }
  }, [returnTarget]);

  // Graceful-degradation fallback target (auto-login failed): continue to
  // `/login`, forwarding any allowlisted cross-origin returnUrl so the manual
  // sign-in still completes the cross-domain handoff. Same open-redirect gate;
  // never trusts the raw param. Always a same-app relative path.
  const nextDestination = useMemo(
    () => postRegisterDestination(rawReturn),
    [rawReturn],
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dob, setDob] = useState('');
  const [displayName, setDisplayName] = useState('');

  const [fieldErrors, setFieldErrors] = useState<RegisterFieldErrors>({});
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [serverMessage, setServerMessage] = useState<string | undefined>();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerMessage(undefined);

    // Client-side validation mirrors the server (inline pre-submit feedback).
    // Age is intentionally NOT checked here — the server owns the COPPA gate.
    const errors = validateRegisterForm({
      email,
      password,
      confirmPassword,
      dob,
      displayName,
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setStatus('submitting');
    // The password is passed straight to the API client and never logged/stored.
    const result = await registerIdentity({
      email,
      password,
      dob,
      display_name: displayName || undefined,
    });

    if (result.kind === 'success') {
      // bl-792 — register mints NO session, so we AUTO-LOGIN with the
      // just-entered credentials (identity-service allows login for an
      // unverified email — verified live 2026-06-22) and carry the new user
      // INTO the site via the same cross-domain `?code=` handoff `/login` uses.
      // For a brand-new, unverified, unlinked user that lands them on the bl-795
      // `/onboarding/verify-email` step (then II-link, then profile). We do NOT
      // dump them on `/login`.
      setStatus('success');
      const loginResult = await login({
        email,
        password,
        return_origin: returnOrigin,
      });

      // Cross-domain success: redirect to the server-minted `<origin>?code=…`
      // handoff (INTO the site). A direct/central success (no return_origin →
      // no `redirect`) honors the resolved target so the user still lands in-site.
      if (loginResult.kind === 'success') {
        window.location.assign(loginResult.redirect ?? returnTarget);
        return;
      }

      // Graceful degradation — any non-success auto-login outcome (mfa-required,
      // unauthorized, locked, network/5xx error) must NOT strand the user on
      // /register. The account WAS created; surface a friendly "please sign in"
      // banner and hand off to /login (carrying any allowlisted returnUrl).
      setStatus('autologin-fallback');
      window.location.assign(nextDestination);
      return;
    }

    if (result.kind === 'validation') {
      setServerMessage(result.message);
    }
    setStatus(result.kind);
  }

  return (
    <main className="register" data-testid="register-page">
      <section className="register__card">
        <h1 className="register__title">Create your account</h1>
        <p className="register__subtitle">
          Register once for Hello World DAO and FounderyOS.
        </p>

        {status === 'under13' && (
          <p className="register__banner register__banner--block" data-testid="register-error-under13">
            We&apos;re sorry, but you are not eligible to create an account.
          </p>
        )}
        {status === 'minor' && (
          <p className="register__banner" data-testid="register-error-minor">
            Accounts for people under 18 are not yet available. Please check back later.
          </p>
        )}
        {status === 'conflict' && (
          <p className="register__banner" data-testid="register-error-conflict">
            Looks like you already have an account with this email. Sign-in is coming soon.
          </p>
        )}
        {status === 'error' && (
          <p className="register__banner" data-testid="register-error-generic">
            Something went wrong on our end. Give it another try.
          </p>
        )}
        {status === 'validation' && (
          <p className="register__banner" data-testid="register-error-validation">
            {serverMessage ?? 'Please check the form and try again.'}
          </p>
        )}
        {status === 'autologin-fallback' && (
          <p className="register__banner" data-testid="register-autologin-fallback">
            You&apos;re all set. Sign in to continue.
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate data-testid="register-form">
          <label className="register__field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email"
            />
            {fieldErrors.email && (
              <span className="register__error" data-testid="register-email-error">
                {fieldErrors.email}
              </span>
            )}
          </label>

          <label className="register__field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password"
            />
            {fieldErrors.password && (
              <span className="register__error" data-testid="register-password-error">
                {fieldErrors.password}
              </span>
            )}
          </label>

          <label className="register__field">
            <span>Confirm password</span>
            <input
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="register-confirm-password"
            />
            {fieldErrors.confirmPassword && (
              <span
                className="register__error"
                data-testid="register-confirm-password-error"
              >
                {fieldErrors.confirmPassword}
              </span>
            )}
          </label>

          <label className="register__field">
            <span>Date of birth</span>
            <input
              type="date"
              name="date-of-birth"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              data-testid="register-date-of-birth"
            />
            {fieldErrors.dob && (
              <span className="register__error" data-testid="register-dob-error">
                {fieldErrors.dob}
              </span>
            )}
          </label>

          <label className="register__field">
            <span>Display name (optional)</span>
            <input
              type="text"
              name="display-name"
              autoComplete="nickname"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="register-display-name"
            />
          </label>

          <button
            type="submit"
            className="register__submit"
            disabled={status === 'submitting'}
            data-testid="register-submit"
          >
            {status === 'submitting' ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="register__footer">
          Already have an account?{' '}
          {/* Sign-in is live; send existing users straight to /login (carrying any
              allowlisted returnUrl via nextDestination so the cross-domain handoff
              is preserved). */}
          <Link to={nextDestination} data-testid="register-login-link">
            Log in
          </Link>
        </p>
      </section>
    </main>
  );
}
